import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth.jsx";
import { Toaster } from "sonner";

import Landing from "./pages/Landing.jsx";
import Login from "./pages/Login.jsx";
import Signup from "./pages/Signup.jsx";
import AuthCallback from "./pages/AuthCallback.jsx";
import Pricing from "./pages/Pricing.jsx";
import AppLayout from "./components/AppLayout.jsx";
import Dashboard from "./pages/Dashboard.jsx";
import Protocols from "./pages/Protocols.jsx";
import ProtocolDetail from "./pages/ProtocolDetail.jsx";
import Drugs from "./pages/Drugs.jsx";
import DrugDetail from "./pages/DrugDetail.jsx";
import Interactions from "./pages/Interactions.jsx";
import Calculators from "./pages/Calculators.jsx";
import CalculatorDetail from "./pages/CalculatorDetail.jsx";
import PaediatricDrugs from "./pages/PaediatricDrugs.jsx";
import AntibioticGuide from "./pages/AntibioticGuide.jsx";
import QuickReference from "./pages/QuickReference.jsx";
import ICD10 from "./pages/ICD10.jsx";
import Favorites from "./pages/Favorites.jsx";
import Search from "./pages/Search.jsx";
import Settings from "./pages/Settings.jsx";
import CompleteProfile from "./pages/CompleteProfile.jsx";
import PaymentCallback from "./pages/PaymentCallback.jsx";
import PaymentCancelled from "./pages/PaymentCancelled.jsx";
import Admin from "./pages/Admin.jsx";
import PrivacyPolicy from "./pages/PrivacyPolicy.jsx";
import MedicalDisclaimer from "./pages/MedicalDisclaimer.jsx";
import About from "./pages/About.jsx";
import ForgotPassword from "./pages/ForgotPassword.jsx";
import ResetPassword from "./pages/ResetPassword.jsx";
import Terms from "./pages/Terms.jsx";
import CookiePolicy from "./pages/CookiePolicy.jsx";
import Feedback from "./pages/Feedback.jsx";
import CookieBanner from "./components/CookieBanner.jsx";
import InstallPrompt from "./components/InstallPrompt.jsx";
import RouteAnalytics from "./components/RouteAnalytics.jsx";
import ClarityLoader from "./components/ClarityLoader.jsx";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-sm text-slate-500">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (!user || user.role !== "admin") return <Navigate to="/app/dashboard" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <RouteAnalytics />
          <ClarityLoader />
          <Toaster position="top-right" richColors />
          <CookieBanner />
          <InstallPrompt />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/complete-profile" element={<Protected><CompleteProfile /></Protected>} />
            <Route path="/payment/callback" element={<PaymentCallback />} />
            <Route path="/payment-success" element={<PaymentCallback />} />
            <Route path="/payment-cancelled" element={<PaymentCancelled />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/disclaimer" element={<MedicalDisclaimer />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/feedback" element={<Feedback />} />
            <Route path="/about" element={<About />} />

            <Route path="/app" element={<Protected><AppLayout /></Protected>}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="protocols" element={<Protocols />} />
              <Route path="protocols/:id" element={<ProtocolDetail />} />
              <Route path="drugs" element={<Drugs />} />
              <Route path="drugs/:id" element={<DrugDetail />} />
              <Route path="interactions" element={<Interactions />} />
              <Route path="calculators" element={<Calculators />} />
              <Route path="calculators/:id" element={<CalculatorDetail />} />
              <Route path="paediatric-drugs" element={<PaediatricDrugs />} />
              <Route path="antibiotic-guide" element={<AntibioticGuide />} />
              <Route path="quick-reference" element={<QuickReference />} />
              <Route path="icd10" element={<ICD10 />} />
              <Route path="favorites" element={<Favorites />} />
              <Route path="search" element={<Search />} />
              <Route path="settings" element={<Settings />} />
              <Route path="admin" element={<AdminOnly><Admin /></AdminOnly>} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
